import type { FilterSchema } from '@/shared/components/filter/validation'
import type { BIRevenueResponse, FilterRuleModel } from '@/shared/generated'
import type { WidgetPeriods } from '../types'

import React from 'react'
import { getPercentOf } from '@/pages/dashboard/lib/helpers'
import { addDays, format } from 'date-fns'
import { prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBIRevenueAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { usePricingAlertsAnnotationsQuery } from '@/entities/price-alert'

import { Operators } from '@/shared/components/query-builder'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { DateFormat } from '@/shared/tools/dates'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

type DataRow = {
	revenue: number
	assortment: number
}
export default function NumericBlockedForRepricingWidget({
	className,
	commonPeriods,
	commonFilters,
}: {
	className?: string
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const start_date = format(
		addDays(new Date(commonPeriods.main.end_date), -14),
		DateFormat.system
	)
	const end_date = commonPeriods.main.end_date
	const currency = useSelectedCurrency()
	const biCommonQuery = {
		currency,
		start_date,
		end_date,
		revenue_start_date: start_date,
		revenue_end_date: end_date,
		label: 'NumericBlockedForRepricing',
		metrics: ['revenue', 'assortment'],
		date_aggregation: null,
		dimensions: [],
		skip_dates: true,
	}
	const { data: annotations } = usePricingAlertsAnnotationsQuery()
	const biQueryConfig = {
		enabled: !!annotations?.length,
		select: (resp: BIRevenueResponse | null) =>
			zipSeries<DataRow>(resp!.meta, resp!.data)[0],
	}
	const totalRevenueQuery = useBIRevenueAnalyticQuery<DataRow>(
		{
			...biCommonQuery,
			filters: [
				{
					name: 'optimization_lag',
					operation: 'eq',
					value: 1,
				},
				{ name: 'is_repricing_today', operation: 'bl', value: true },
				...commonFilters,
			],
		},
		biQueryConfig
	)
	const { isLoading, data, isError } = useBIRevenueAnalyticQuery<{
		assortment: number
		revenue: number
	}>(
		{
			...biCommonQuery,
			filters: [
				{
					name: 'optimization_lag',
					operation: Operators.EQ,
					value: 1,
				},
				{ name: 'is_repricing_today', operation: 'bl', value: true },
				{
					name: 'price_change_status',
					operation: Operators.IS,
					value: 'not_changed',
				},
				{
					name: 'pricing_line_notifications',
					operation: Operators.ANY_OF,
					value: annotations
						?.filter((annotataion) =>
							['critical', 'unassigned'].includes(annotataion.level)
						)
						?.map(prop('id')),
				} as FilterRuleModel,
				...commonFilters,
			],
		},
		biQueryConfig
	)
	const { revenue, assortment } = data || {}
	const revenuePercent = getPercentOf(
		revenue || 0,
		totalRevenueQuery?.data?.revenue || 0
	)

	return (
		<Widget className={cn('relative flex flex-auto flex-col', className)}>
			<WidgetHeader
				title={t('widget.blocked_for_repricing.title')}
				tooltip={t('widget.blocked_for_repricing.tooltip')}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='mt-auto truncate'>
				{isLoading ? (
					<Skeleton className='col-span-2 mt-auto h-10 w-32' />
				) : !assortment ? (
					<WidgetEmptyState size='small' />
				) : (
					<div className='mt-auto flex flex-col space-y-1'>
						<div className='flex items-center space-x-2'>
							<span className='truncate text-xs text-muted-foreground'>
								{formatNumber(assortment)} SKUs
							</span>
						</div>
						<span className='flex items-center truncate text-3xl font-semibold'>
							{formatMoney(revenue as number, currency, NumberFormats.Compact)}
							{!!totalRevenueQuery?.data?.revenue && (
								<Badge size='sm' className='ml-2'>
									{formatNumber(revenuePercent, NumberFormats.Percent)}
								</Badge>
							)}
						</span>
					</div>
				)}
			</WidgetContent>
		</Widget>
	)
}
